import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/LanguageContext";
import BrandMark from "@/components/BrandMark";
import {
  CheckCircle, XCircle, ArrowRight, Buildings, Users, ChartBar,
  Receipt, CalendarBlank,
  Translate, Lightning, Warning, Play, Phone,
} from "@phosphor-icons/react";

const SECTORS = [
  { id: "clients", icon: Users, titleEs: "Clientes y presupuestos", titleEn: "Clients and quotes",
    descEs: "Crea clientes, haz presupuestos en minutos y envíalos por WhatsApp o email.",
    descEn: "Create clients, make quotes in minutes and send them via WhatsApp or email.",
    badgeEs: "Incluido en todos los planes",
    badgeEn: "Included in all plans" },
  { id: "invoicing", icon: Receipt, titleEs: "Facturas y cobros", titleEn: "Invoices and payments",
    descEs: "Factura con QR (Veri*Factu), controla cobros pendientes y pagos parciales.",
    descEn: "QR invoices (Veri*Factu), track pending payments and partial collections.",
    badgeEs: "Veri*Factu: QR + registro",
    badgeEn: "Veri*Factu: QR + record" },
  { id: "expenses", icon: ChartBar, titleEs: "Gastos", titleEn: "Expenses",
    descEs: "Escanea tickets con IA, concilia bancos y genera el borrador del Modelo 303.",
    descEn: "Scan receipts with AI, reconcile bank accounts and generate Modelo 303 draft.",
    badgeEs: "OCR con IA incluido",
    badgeEn: "AI OCR included" },
  { id: "projects", icon: CalendarBlank, titleEs: "Proyectos y trabajos", titleEn: "Projects and jobs",
    descEs: "Planifica trabajos, sube fotos, firma digital del cliente y control de rentabilidad.",
    descEn: "Plan jobs, upload photos, client digital signature and profitability tracking.",
    badgeEs: "Disponible en plan Avanzado",
    badgeEn: "Available in Advanced plan" },
  { id: "catalog", icon: Buildings, titleEs: "Catálogo y materiales", titleEn: "Catalog and materials",
    descEs: "Lista de materiales, costes por unidad y referencias propias o de proveedor.",
    descEn: "Materials list, unit costs and references from your own or your supplier.",
    badgeEs: "Disponible en plan Avanzado",
    badgeEn: "Available in Advanced plan" },
];

const FEATURES = [
  { icon: Users, titleEs: "Clientes y presupuestos", titleEn: "Clients and quotes",
    descEs: "Crea clientes, haz presupuestos en minutos y conviértelos a factura en un clic.",
    descEn: "Create clients, make quotes in minutes and convert to invoice in one click." },
  { icon: Receipt, titleEs: "Facturas con QR", titleEn: "QR invoices",
    descEs: "Factura con código QR (Veri*Factu), controla cobros y pagos parciales.",
    descEn: "QR invoices (Veri*Factu), track payments and partial collections." },
  { icon: ChartBar, titleEs: "Gastos y control", titleEn: "Expenses and control",
    descEs: "OCR de tickets, conciliación bancaria y borrador del Modelo 303 trimestral.",
    descEn: "Receipt OCR, bank reconciliation and quarterly Modelo 303 draft." },
  { icon: CalendarBlank, titleEs: "Proyectos y trabajos", titleEn: "Projects and jobs",
    descEs: "Organiza trabajos, sube fotos, firma digital del cliente y mira la rentabilidad.",
    descEn: "Organize jobs, upload photos, client digital signature and check profitability." },
];

const PLANS = [
  { id: "solo", name: "Básico", price: 19,
    badgeEs: "Para autónomos y freelancers", badgeEn: "For freelancers and sole traders",
    featuresEs: [
      "1 usuario",
      "Hasta 100 clientes",
      "50 presupuestos y facturas / mes",
      "Factura con QR (Veri*Factu)",
      "Control de gastos básico",
      "Soporte por email",
    ],
    featuresEn: [
      "1 user",
      "Up to 100 clients",
      "50 quotes and invoices / month",
      "QR invoice (Veri*Factu)",
      "Basic expense tracking",
      "Email support",
    ] },
  { id: "team", name: "Estándar", price: 39, highlighted: true,
    badgeEs: "El más popular", badgeEn: "Most popular",
    featuresEs: [
      "Hasta 5 usuarios incluidos",
      "+9€ por usuario extra/mes",
      "Todo ilimitado (clientes, presupuestos, facturas)",
      "OCR de gastos (IA)",
      "Conciliación bancaria (CSV)",
      "Modelo 303 trimestral (borrador)",
      "Soporte prioritario",
    ],
    featuresEn: [
      "Up to 5 users included",
      "+€9 per extra user/month",
      "Everything unlimited (clients, quotes, invoices)",
      "AI expense OCR",
      "Bank reconciliation (CSV)",
      "Quarterly Modelo 303 (draft)",
      "Priority support",
    ] },
  { id: "pro", name: "Avanzado", price: 79,
    badgeEs: "Para empresas en crecimiento", badgeEn: "For growing companies",
    featuresEs: [
      "Usuarios ilimitados",
      "Todo del plan Estándar",
      "Proyectos y catálogo de materiales",
      "Multi-empresa",
      "API & webhooks",
      "Soporte dedicado",
    ],
    featuresEn: [
      "Unlimited users",
      "Everything in Estándar",
      "Projects and materials catalog",
      "Multi-company",
      "API & webhooks",
      "Dedicated support",
    ] },
];

const PAINS = [
  { es: "Presupuestos que se rehacen desde cero en Word o Excel.", en: "Quotes rebuilt from scratch in Word or spreadsheets." },
  { es: "Fotos y documentos perdidos en WhatsApp sin relación con el proyecto.", en: "Photos and documents lost in WhatsApp with no link to the project." },
  { es: "Facturas que se emiten tarde o se quedan sin cobrar.", en: "Invoices issued late or left unpaid." },
  { es: "Información del cliente desperdigada en WhatsApp, email y libretas.", en: "Client information scattered across WhatsApp, email and notebooks." },
  { es: "Gastos en tickets sueltos que nunca se registran.", en: "Expenses on loose receipts that never get logged." },
  { es: "Software potente pero caro, pesado y poco móvil.", en: "Powerful software that's expensive, heavy and weak on mobile." },
];

const COMPARE = [
  {
    id: "today",
    titleEs: "Hoy: Excel + WhatsApp",
    titleEn: "Today: spreadsheets + WhatsApp",
    tagEs: "Lo que usas ahora",
    tagEn: "What you use now",
    rowsEs: [
      [false, "Reescribes todo a mano cada vez"],
      [false, "Documentos perdidos en chats"],
      [false, "Sin control de cobros ni pagos"],
      [false, "Veri*Factu lo resuelves aparte"],
      [false, "Gratis, pero te cuesta horas"],
    ],
    rowsEn: [
      [false, "You retype everything by hand each time"],
      [false, "Documents lost in chats"],
      [false, "No payment or collection tracking"],
      [false, "Veri*Factu handled separately"],
      [false, "Free, but it costs you hours"],
    ],
  },
  {
    id: "erp",
    titleEs: "ERP genérico caro",
    titleEn: "Expensive generic ERP",
    tagEs: "La alternativa cara",
    tagEn: "The expensive alternative",
    rowsEs: [
      [false, "Potente, pero complejo de usar"],
      [false, "Cientos de € al mes"],
      [false, "Implantación de semanas"],
      [false, "Incómodo en el móvil"],
      [false, "Permanencia y contratos largos"],
    ],
    rowsEn: [
      [false, "Powerful, but complex to use"],
      [false, "Hundreds of € per month"],
      [false, "Weeks of onboarding"],
      [false, "Clunky on mobile"],
      [false, "Lock-in and long contracts"],
    ],
  },
  {
    id: "factlify",
    highlighted: true,
    titleEs: "Factlify",
    titleEn: "Factlify",
    tagEs: "Simple, móvil y asequible",
    tagEn: "Simple, mobile and affordable",
    rowsEs: [
      [true, "Presupuesto → factura en un clic"],
      [true, "App móvil con firma y fotos"],
      [true, "Factura con QR (Veri*Factu)"],
      [true, "Desde 19€/mes, sin permanencia"],
      [true, "Listo en menos de 10 minutos"],
    ],
    rowsEn: [
      [true, "Quote → invoice in one click"],
      [true, "Mobile app with signature and photos"],
      [true, "QR invoice (Veri*Factu)"],
      [true, "From €19/mo, no lock-in"],
      [true, "Ready in under 10 minutes"],
    ],
  },
];

export default function Landing() {
  const { lang, setLang } = useI18n();
  const T = (es, en) => (lang === "es" ? es : en);
  return (
    <div className="min-h-screen bg-paper" data-testid="landing-page">
      <div className="h-1.5 w-full bg-safety shrink-0" aria-hidden="true" data-testid="brand-stripe" />
      {/* Urgency bar — Ley Crea y Crece */}
      <div className="bg-ink text-white text-xs font-mono font-bold uppercase tracking-widest py-2 px-4 text-center flex items-center justify-center gap-2">
        <Warning size={13} weight="bold" className="shrink-0" />
        {T(
          "Factura electrónica B2B y Veri*Factu llegan a España por fases. Factlify se está preparando.",
          "B2B e-invoicing and Veri*Factu are being phased in across Spain. Factlify is getting ready."
        )}
        <a href="#pricing" className="underline underline-offset-2 ml-1 hover:text-white/80">{T("Ver planes →", "See plans →")}</a>
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b-2 border-ink bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Factlify">
            <BrandMark stripe="none" size="md" />
          </Link>
          <div className="flex items-center gap-2">
            <a href="#features" className="hidden sm:block text-sm font-medium text-steel hover:text-ink">{T("Funciones", "Features")}</a>
            <a href="#solutions" className="hidden sm:block text-sm font-medium text-steel hover:text-ink">{T("Soluciones", "Solutions")}</a>
            <a href="#pricing" className="hidden sm:block text-sm font-medium text-steel hover:text-ink">{T("Precios", "Pricing")}</a>
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} data-testid="landing-lang"
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-steel ml-3">
              <Translate size={14} weight="bold" /> {lang.toUpperCase()}
            </button>
            <Link to="/login" data-testid="landing-login"
              className="ml-2 px-4 py-2 border-2 border-ink font-bold uppercase tracking-wider text-xs hover:bg-ink hover:text-white">
              {T("Entrar", "Log in")}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b-2 border-ink bg-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "linear-gradient(135deg, transparent 48%, white 49%, white 51%, transparent 52%), linear-gradient(-135deg, transparent 48%, white 49%, white 51%, transparent 52%)",
          backgroundSize: "60px 60px",
        }} />
        <div className="relative max-w-6xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.22em] text-safety mb-5">
              {T("FACTURACIÓN Y GESTIÓN SIMPLE · AUTÓNOMOS Y PYMES", "SIMPLE INVOICING & MANAGEMENT · FREELANCERS AND SMES")}
            </p>
            <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tight leading-[0.95]">
              {lang === "es" ? (
                <>
                  Presupuesta y{" "}
                  <span
                    className="inline-block px-2 sm:px-3 bg-safety text-white"
                    style={{ transform: "skewY(-1deg)" }}
                  >
                    factura
                  </span>{" "}
                  tu oficio.
                  <br />
                  <span className="text-white/70">Desde el móvil, en minutos.</span>
                </>
              ) : (
                <>
                  Quote and{" "}
                  <span
                    className="inline-block px-2 sm:px-3 bg-safety text-white"
                    style={{ transform: "skewY(-1deg)" }}
                  >
                    invoice
                  </span>{" "}
                  your trade.
                  <br />
                  <span className="text-white/70">From your phone, in minutes.</span>
                </>
              )}
            </h1>
            <p className="mt-6 max-w-xl text-white/80 text-base lg:text-lg leading-relaxed">
              {lang === "es" ? (
                <>
                  Hecho para <b className="text-white">reformistas, fontaneros, electricistas, pintores y carpinteros</b>.
                  Gestiona clientes, haz presupuestos, factura y controla gastos. Sin implantación, sin permanencia.
                </>
              ) : (
                <>
                  Built for <b className="text-white">builders, plumbers, electricians, painters and carpenters</b>.
                  Manage clients, create quotes, invoice and track expenses. No setup, no lock-in.
                </>
              )}
            </p>
            <div className="mt-8 flex flex-col gap-2.5 max-w-sm">
              <Link
                to="/register"
                data-testid="hero-start"
                className="inline-flex items-center justify-center gap-2 px-5 min-h-[52px] bg-safety text-white border-2 border-safety font-bold text-sm uppercase tracking-wider hover:bg-safety/90"
              >
                {T("Probar gratis 14 días", "Try free for 14 days")}
                <ArrowRight size={14} weight="bold" />
              </Link>
              <p className="text-xs text-white/60 text-center">
                {lang === "es" ? (
                  <>
                    Sin tarjeta. Sin permanencia. Después <b className="text-white">19€/mes</b>.
                  </>
                ) : (
                  <>
                    No card. No lock-in. Then <b className="text-white">€19/mo</b>.
                  </>
                )}
              </p>
              <a
                href="#pricing"
                className="text-center text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white underline underline-offset-2"
              >
                {T("Ver planes", "See plans")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-3">
            {[
              T("CLIENTES", "CLIENTS"),
              T("PRESUPUESTOS", "QUOTES"),
              T("FACTURAS", "INVOICES"),
              T("GASTOS", "EXPENSES"),
            ].map((x) => (
              <div key={x} className="border-2 border-white/30 px-3 py-4 text-center">
                <div className="font-display font-black text-white text-lg">{x}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats — Design handoff */}
      <section className="border-t-2 border-b-2 border-ink bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-12 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { k: "4", v: T("Módulos esenciales", "Essential modules") },
            { k: "100%", v: T("En la nube", "Cloud-based") },
            { k: "19€", v: T("Al mes", "Per month") },
            { k: "0 %", v: T("Permanencia", "Lock-in") },
          ].map((s) => (
            <div key={s.v}>
              <div className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink">{s.k}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-steel mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b-2 border-ink bg-white">
        <div className="max-w-6xl mx-auto px-5 py-5 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-x-10 gap-y-4 sm:gap-y-3">
          {[
            { num: "14 días", desc: T("gratis, sin tarjeta", "free, no card") },
            { num: "< 10 min", desc: T("para empezar", "to get started") },
            { num: "Veri*Factu", desc: T("Factura con QR en el PDF", "QR invoice in the PDF") },
            { num: "Mobile-first", desc: T("trabaja desde cualquier sitio", "work from anywhere") },
          ].map((s, i) => (
            <div key={i} className="flex sm:flex-row items-center gap-3 justify-center sm:justify-start">
              <div className="text-center sm:text-left">
                <div className="font-display font-black text-xl tracking-tight text-ink">{s.num}</div>
                <div className="text-xs text-steel font-mono uppercase tracking-widest">{s.desc}</div>
              </div>
              {i < 3 && <div className="hidden sm:block w-px h-8 bg-slate-200" />}
            </div>
          ))}
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="border-b-2 border-ink">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="mb-8">
            <div className="overline">{T("SOLUCIONES", "SOLUTIONS")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Todo lo que necesitas para gestionar tu negocio.", "Everything you need to manage your business.")}
            </h2>
            <p className="text-steel mt-2 max-w-2xl">
              {T("Elige los módulos que necesitas. Empieza con facturación y crece a tu ritmo.",
                 "Choose the modules you need. Start with invoicing and grow at your pace.")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTORS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="brutal-card bg-white p-5 hover:shadow-brutal transition-all flex flex-col"
                  data-testid={`sector-${s.id}`}>
                  <Icon size={28} weight="bold" className="text-safety mb-3" />
                  <div className="font-display font-black text-lg mb-1">{lang === "es" ? s.titleEs : s.titleEn}</div>
                  <div className="text-sm text-steel">{lang === "es" ? s.descEs : s.descEn}</div>
                  <div className={`mt-3 pt-3 border-t border-slate-200 text-[10px] font-mono uppercase tracking-widest leading-snug ${s.badgeEs.includes("Incluido") || s.badgeEs.includes("Veri") || s.badgeEs.includes("OCR") ? "text-safety" : "text-steel"}`}>
                    {lang === "es" ? s.badgeEs : s.badgeEn}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-b-2 border-ink bg-white">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="mb-8">
            <div className="overline">{T("POR QUÉ AHORA", "WHY NOW")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Tu negocio no necesita otro Excel bonito.", "Your business doesn't need another pretty spreadsheet.")}
            </h2>
            <p className="text-steel mt-2 max-w-2xl">
              {T("Necesita un sistema que conecte oficina, equipo y administración sin llamadas innecesarias.",
                 "It needs a system that connects office, team and admin without unnecessary calls.")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAINS.map((pain, i) => (
              <div key={i} className="border-2 border-slate-200 p-4 bg-paper">
                <div className="text-[10px] font-mono uppercase tracking-widest text-safety mb-2">0{i + 1}</div>
                <div className="font-semibold text-sm text-ink">{lang === "es" ? pain.es : pain.en}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativa */}
      <section className="border-b-2 border-ink bg-paper">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="mb-10 text-center">
            <div className="overline">{T("POR QUÉ FACTLIFY Y NO OTRA COSA", "WHY FACTLIFY OVER THE REST")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Compara con lo que usas hoy.", "Compare it with what you use today.")}
            </h2>
            <p className="text-steel mt-2 max-w-2xl mx-auto">
              {T("La hoja de cálculo es gratis pero te roba horas. El ERP genérico es caro y complejo. Factlify está hecho para tu negocio.",
                 "Spreadsheets are free but steal your hours. Generic ERPs are expensive and complex. Factlify is built for your business.")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {COMPARE.map((c) => (
              <div key={c.id}
                data-testid={`compare-${c.id}`}
                className={`brutal-card bg-white p-6 ${c.highlighted ? "border-t-4 border-safety shadow-brutal" : "border-t-4 border-slate-300"}`}>
                <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${c.highlighted ? "text-safety" : "text-steel"}`}>
                  {T(c.tagEs, c.tagEn)}
                </div>
                <div className="font-display font-black text-xl uppercase mb-4">{T(c.titleEs, c.titleEn)}</div>
                <ul className="space-y-2.5 text-sm">
                  {(lang === "es" ? c.rowsEs : c.rowsEn).map(([ok, text], i) => (
                    <li key={i} className="flex items-start gap-2">
                      {ok
                        ? <CheckCircle size={16} weight="bold" className="text-safety mt-0.5 shrink-0" />
                        : <XCircle size={16} weight="bold" className="text-slate-300 mt-0.5 shrink-0" />}
                      <span className={ok ? "text-ink" : "text-steel"}>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b-2 border-ink">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="mb-10">
            <div className="overline">{T("FUNCIONES", "FEATURES")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Del presupuesto a la factura cobrada.", "From quote to paid invoice.")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="border-l-4 border-safety pl-4 py-2" data-testid={`feature-${i}`}>
                  <Icon size={22} weight="bold" className="text-ink mb-2" />
                  <div className="font-display font-bold text-lg mb-1">{lang === "es" ? f.titleEs : f.titleEn}</div>
                  <div className="text-sm text-steel">{lang === "es" ? f.descEs : f.descEn}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Screenshots reales */}
      <section className="border-b-2 border-ink bg-paper">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center mb-10">
            <div className="overline">{T("ASÍ SE VE POR DENTRO", "THIS IS WHAT IT LOOKS LIKE")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Míralo antes de probarlo.", "Watch it before you try it.")}
            </h2>
            <p className="text-steel mt-2 max-w-xl mx-auto text-sm">
              {T("Capturas reales de la app — no son mockups.",
                 "Real app screenshots — not mockups.")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { src: "/screenshots/smoke_desktop_01_dashboard.png", alt: "Dashboard Factlify",
                labelEs: "Dashboard — Acción inmediata", labelEn: "Dashboard — Immediate action" },
              { src: "/screenshots/smoke_desktop_05_projects.png", alt: "Proyectos kanban Factlify",
                labelEs: "Proyectos — Kanban visual", labelEn: "Projects — Visual kanban" },
              { src: "/screenshots/smoke_desktop_04_invoices.png", alt: "Facturas Factlify",
                labelEs: "Facturas — Control financiero", labelEn: "Invoices — Financial control" },
            ].map((shot) => (
              <div key={shot.src} className="border-2 border-ink bg-white overflow-hidden group">
                <div className="bg-ink text-white text-[10px] font-mono font-bold uppercase tracking-widest px-3 py-1.5">
                  {T(shot.labelEs, shot.labelEn)}
                </div>
                <div className="aspect-[4/3] overflow-hidden bg-paper">
                  <img src={shot.src} alt={shot.alt} loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-ink hover:text-safety transition-colors">
              <Lightning size={14} weight="bold" /> {T("Probarlo gratis 14 días →", "Try it free for 14 days →")}
            </Link>
          </div>
        </div>
      </section>

      {/* Demo / CTA directa */}
      <section className="border-b-2 border-ink bg-ink text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-white/50 mb-3">{T("VER EN ACCIÓN", "SEE IT IN ACTION")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight leading-tight">
              {T(<>Un autónomo crea un presupuesto,<br /><span className="text-safety">lo envía al cliente en 2 minutos</span><br />y factura con QR desde el móvil.</>,
                 <>A freelancer creates a quote,<br /><span className="text-safety">sends it to the client in 2 minutes</span><br />and invoices with QR from mobile.</>)}
            </h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-lg">
              {T("Sin volver a la oficina. Sin reescribir nada. Con fotos del trabajo, firma digital del cliente y factura lista al terminar.",
                 "Without going back to the office. Without retyping anything. With work photos, client digital signature and invoice ready on completion.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login"
                className="bg-safety text-white border-2 border-safety font-bold uppercase tracking-wider px-6 py-3 flex items-center gap-2 hover:brightness-110">
                <Play size={15} weight="fill" /> {T("Probar gratis 14 días", "Try free 14 days")}
              </Link>
              <a href="mailto:soporte@factlify.es?subject=Demo%20Factlify"
                className="bg-white/5 border-2 border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 flex items-center gap-2 hover:bg-white/10">
                <Phone size={15} weight="bold" /> {T("Pedir demo personalizada", "Request a personal demo")}
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { step: "01", es: "Crea un cliente en 20 segundos", en: "Create a client in 20 seconds" },
              { step: "02", es: "Haz un presupuesto desde la app", en: "Create a quote from the app" },
              { step: "03", es: "Envíalo por email o WhatsApp", en: "Send it via email or WhatsApp" },
              { step: "04", es: "El cliente acepta → proyecto creado", en: "Client accepts → project created" },
              { step: "05", es: "Tu equipo trabaja, sube fotos y firma", en: "Your team works, uploads photos and signs" },
              { step: "06", es: "Factura con QR y cobra al instante", en: "Invoice with QR and collect instantly" },
            ].map((s) => (
              <div key={s.step} className="border border-white/20 p-3 bg-white/5">
                <div className="font-mono text-[10px] text-safety font-bold mb-1">{s.step}</div>
                <div className="text-xs text-white/80 leading-snug">{T(s.es, s.en)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b-2 border-ink bg-paper">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center mb-10">
            <div className="overline">{T("PRECIOS", "PRICING")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Planes para cada etapa de tu negocio.", "Plans for every stage of your business.")}
            </h2>
            <p className="text-steel mt-2 max-w-2xl mx-auto">
              {T("Empieza sin implantación. Crece desde autónomo hasta equipo, sin complicaciones.",
                 "Start without setup. Grow from freelancer to a team, no complications.")}
            </p>
            <div className="mt-3 inline-block text-xs font-mono font-bold uppercase tracking-widest text-ink border-2 border-ink px-3 py-1">
              {T("14 días de prueba gratis · Sin tarjeta", "14-day free trial · No credit card")}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {PLANS.map((p) => (
              <div key={p.id}
                data-testid={`landing-plan-${p.id}`}
                className={`brutal-card bg-white p-6 relative ${p.highlighted ? "border-t-4 border-safety shadow-brutal" : "border-t-4 border-ink"}`}>
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-safety text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">
                    {lang === "es" ? p.badgeEs : p.badgeEn}
                  </div>
                )}
                <div className="font-display font-black text-2xl uppercase">{p.name}</div>
                {!p.highlighted && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-steel mt-0.5">
                    {lang === "es" ? p.badgeEs : p.badgeEn}
                  </div>
                )}
                <div className="font-display font-black text-5xl tracking-tight mt-3">
                  €{p.price}<span className="text-sm font-normal text-steel">/{T("mes", "mo")}</span>
                </div>
                <div className="text-[11px] text-steel mt-1 font-mono">
                  {T("IVA incluido · Sin permanencia", "VAT included · No commitment")}
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  {(lang === "es" ? p.featuresEs : p.featuresEn).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle size={16} weight="bold" className="text-ink mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/login"
                  className={`block text-center mt-6 px-4 py-3 border-2 font-bold uppercase tracking-wider text-sm ${
                    p.highlighted ? "bg-safety text-white border-safety hover:brightness-110" : "bg-ink text-white border-ink hover:bg-ink hover:border-ink"
                  }`}>
                  {T("Empezar gratis", "Start free")}
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-3xl mx-auto border-2 border-ink bg-white p-6 text-center" data-testid="landing-plan-custom">
            <div className="text-[10px] font-mono uppercase tracking-widest text-safety mb-2">
              {T("PLAN PERSONALIZADO", "CUSTOM PLAN")}
            </div>
            <div className="font-display font-black text-xl mb-2">
              {T("¿Caso especial o equipo grande?", "Special case or large team?")}
            </div>
            <p className="text-steel text-sm mb-4 max-w-xl mx-auto">
              {T(
                "Si tu negocio necesita más usuarios, condiciones a medida o un acompañamiento cercano, lo hablamos contigo.",
                "If your business needs more users, custom conditions or hands-on support, let's talk."
              )}
            </p>
            <a href="mailto:soporte@factlify.es?subject=Plan%20Personalizado%20Factlify"
              className="inline-flex items-center gap-2 px-5 py-3 border-2 border-ink font-bold uppercase tracking-wider text-xs hover:bg-ink hover:text-white">
              <Phone size={14} weight="bold" /> {T("Contáctanos", "Contact us")}
            </a>
          </div>
        </div>
      </section>

      {/* Por qué Factlify */}
      <section className="border-b-2 border-ink bg-white">
        <div className="max-w-5xl mx-auto px-5 py-16">
          <div className="mb-10">
            <div className="overline">{T("POR QUÉ FACTLIFY", "WHY FACTLIFY")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Hecho para tu negocio, no para una multinacional.", "Built for your business, not a corporation.")}
            </h2>
            <p className="text-steel mt-2 max-w-2xl">
              {T("Sin instalación, sin formación cara, sin permanencia. Empiezas a cobrar en el mismo día.",
                 "No installation, no expensive training, no lock-in. You start invoicing on day one.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "📱", titleEs: "Funciona desde el móvil", titleEn: "Works on mobile",
                descEs: "Crea presupuestos, sube fotos y factura sin volver a la oficina.", descEn: "Create quotes, upload photos and invoice without going back to the office." },
              { icon: "⚡", titleEs: "Listo en menos de 10 minutos", titleEn: "Ready in under 10 minutes",
                descEs: "Sin implantación, sin técnico, sin contrato. Te damos 14 días para probarlo.", descEn: "No setup, no technician, no contract. 14 days to try it out." },
              { icon: "🇪🇸", titleEs: "Factura con QR (Veri*Factu)", titleEn: "QR invoice (Veri*Factu)",
                descEs: "Cada factura incluye código QR y un registro encadenado en el PDF. Seguimos adaptándonos conforme avanza la normativa.", descEn: "Each invoice includes a QR code and a chained record in the PDF. We keep adapting as the regulation evolves." },
              { icon: "💶", titleEs: "Precio fijo, sin sorpresas", titleEn: "Fixed price, no surprises",
                descEs: "Sin módulos de pago extra ni costes ocultos. Lo que ves es lo que pagas.", descEn: "No paid add-on modules or hidden fees. What you see is what you pay." },
              { icon: "📊", titleEs: "Rentabilidad por proyecto", titleEn: "Profitability per project",
                descEs: "Coste, venta, beneficio y cobros pendientes de un vistazo por proyecto.", descEn: "Cost, sale, profit and pending payments at a glance per project." },
              { icon: "🔒", titleEs: "Tus datos, solo tuyos", titleEn: "Your data, only yours",
                descEs: "Workspace privado por empresa. Puedes exportar todo en cualquier momento.", descEn: "Private workspace per company. You can export everything at any time." },
            ].map((f, i) => (
              <div key={i} className="brutal-card p-5">
                <div className="text-2xl mb-2">{f.icon}</div>
                <div className="font-bold text-sm mb-1">{T(f.titleEs, f.titleEn)}</div>
                <div className="text-steel text-sm">{T(f.descEs, f.descEn)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-ink bg-paper">
        <div className="max-w-3xl mx-auto px-5 py-16">
          <div className="mb-8">
            <div className="overline">{T("PREGUNTAS FRECUENTES", "FAQ")}</div>
            <h2 className="font-display font-black text-4xl tracking-tight mt-1">
              {T("Lo que suelen preguntar.", "What people usually ask.")}
            </h2>
          </div>
          <div className="space-y-0" data-testid="faq-section">
            {[
              {
                q: T("¿Necesito instalar algo?", "Do I need to install anything?"),
                a: T("No. Factlify es 100% en la nube. Funciona desde el navegador del móvil, la tablet o el ordenador. Sin servidores, sin mantenimiento, sin IT.", "No. Factlify is 100% cloud-based. It works from your mobile browser, tablet or computer. No servers, no maintenance, no IT."),
              },
              {
                q: T("¿Puedo empezar sin meter mis datos de pago?", "Can I start without entering payment details?"),
                a: T("Sí. Los 14 días de prueba gratuita no requieren tarjeta. Solo introduce tu email, crea tu cuenta y empieza a presupuestar.", "Yes. The 14-day free trial requires no credit card. Just enter your email, create your account and start quoting."),
              },
              {
                q: T("¿Qué pasa si supero los límites del plan Básico?", "What happens if I exceed the Básico plan limits?"),
                a: T("Recibirás un aviso cuando te acerques al límite. Puedes subir al plan Estándar en cualquier momento desde la app, sin permanencia.", "You'll get a warning when approaching the limit. You can upgrade to the Estándar plan at any time from the app, with no commitment."),
              },
              {
                q: T("¿Puedo añadir más usuarios al plan Estándar?", "Can I add more users to the Estándar plan?"),
                a: T("El plan Estándar incluye hasta 5 usuarios; los adicionales cuestan 9€/usuario/mes. El plan Avanzado incluye usuarios ilimitados sin coste adicional. Si tienes dudas escríbenos a soporte@factlify.es.", "The Estándar plan includes up to 5 users; extra users cost €9/user/month. The Avanzado plan includes unlimited users at no extra cost. If you have questions email us at soporte@factlify.es."),
              },
              {
                q: T("¿Qué pasa con Veri*Factu y la factura electrónica?", "What about Veri*Factu and e-invoicing?"),
                a: T("Cada factura se genera con código QR y un registro encadenado (hash) en el PDF, en formato Veri*Factu. Estamos completando la adaptación conforme avanza el desarrollo reglamentario; la responsabilidad fiscal final es del usuario y su asesor.", "Each invoice is generated with a QR code and a chained record (hash) in the PDF, in Veri*Factu format. We are completing the adaptation as the regulation develops; final tax responsibility lies with the user and their advisor."),
              },
              {
                q: T("¿Puedo importar mis clientes existentes?", "Can I import my existing clients?"),
                a: T("Sí. Puedes importar clientes desde CSV. Si tu negocio necesita catálogo de materiales o referencias técnicas, puedes configurarlos desde Ajustes.", "Yes. You can import clients from CSV. If your business needs a materials catalog or technical references, you can set them up from Settings."),
              },
              {
                q: T("¿Puedo cancelar cuando quiera?", "Can I cancel anytime?"),
                a: T("Sí, sin permanencia ni penalización. Puedes cancelar tu suscripción desde la app. Seguirás teniendo acceso hasta el final del período ya pagado.", "Yes, no commitment or penalty. You can cancel your subscription from the app. You'll retain access until the end of the already-paid period."),
              },
            ].map((item, i) => (
              <details key={i} className="border-2 border-slate-200 border-b-0 last:border-b-2 bg-white group" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer font-bold text-sm hover:bg-slate-50 select-none list-none">
                  <span>{item.q}</span>
                  <span className="shrink-0 font-mono font-black text-xl text-steel group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 pt-1 text-sm text-steel leading-relaxed border-t border-slate-100">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink text-white">
        <div className="max-w-4xl mx-auto px-5 py-16 text-center">
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
            {T("La gestión de tu negocio, resuelta.", "Your business management, solved.")}
          </h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            {T("Clientes, presupuestos, facturas y gastos. Todo en una app, sin complicaciones.",
               "Clients, quotes, invoices and expenses. All in one app, no complications.")}
          </p>
          <Link to="/login" data-testid="final-cta"
            className="inline-flex items-center gap-2 mt-8 bg-safety text-white border-2 border-safety font-bold uppercase tracking-wider px-8 py-4 text-lg hover:brightness-110">
            {T("Empezar ahora", "Start now")} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-ink">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <img src="/logo-icon.svg" alt="" aria-hidden="true" className="h-5 w-auto" />
            <span className="font-display font-black text-xl tracking-tight text-blueprint">FACTLIFY</span>
            <span className="text-steel font-mono font-normal ml-1 text-xs">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-3 lg:gap-4 text-steel uppercase tracking-widest font-bold flex-wrap">
            <a href="#features" className="hover:text-ink">{T("Funciones", "Features")}</a>
            <a href="#pricing" className="hover:text-ink">{T("Precios", "Pricing")}</a>
            <Link to="/login" className="hover:text-ink">{T("Entrar", "Log in")}</Link>
            <Link to="/legal/privacidad" className="hover:text-ink" data-testid="footer-privacy">{T("Privacidad", "Privacy")}</Link>
            <Link to="/legal/aviso" className="hover:text-ink" data-testid="footer-legal">{T("Aviso legal", "Legal")}</Link>
            <Link to="/legal/cookies" className="hover:text-ink">{T("Cookies", "Cookies")}</Link>
            <Link to="/legal/dpa" className="hover:text-ink" data-testid="footer-dpa">DPA</Link>
            <Link to="/legal/terminos" className="hover:text-ink">{T("Términos", "Terms")}</Link>
            <Link to="/legal/licencias" className="hover:text-ink">{T("Licencias", "Licenses")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
